import _ from 'lodash';
import React from 'react';

import Table from 'components/Table';
import Loading from 'components/Loading';
import ToggleSwitch from 'components/ToggleSwitch';
import ButtonDropdown from 'components/ButtonDropdown';
import InvoiceStatusTag from 'components/InvoiceStatusTag';

import {
  RefundRow,
  InvoiceRow,
  PaymentRow,
  CreditNoteRow,
  OverpaymentRow,
  YearSplitter
} from './components';

import {
  YEAR_SPLITTER,
  getDataWithYearSplitters
} from 'containers/BookingSetDetail/utils';

import switchIcon from 'assets/icons/switch.svg';

import {SCHEDULE_ITEM_TYPES} from 'ducks/tenancy';

import './styles.css';

const INVOICE_TYPE = 'invoice';
const CREDIT_NOTE_TYPE = 'credit_note';
const PAYMENT_TYPE = 'payment';
const REFUND_TYPE = 'refund';
const OVERPAYMENT_TYPE = 'overpayment';

const ScheduleRow = ({
  element,
  index,
  handleRowClick,
  redirectToBookingDetail,
  advancedMode
}) => {
  switch (element.type) {
    case INVOICE_TYPE:
      return (
        <InvoiceRow
          key={index}
          invoice={element}
          handleRowClick={handleRowClick}
          redirectToBookingDetail={redirectToBookingDetail}
          advancedMode={advancedMode}
        />
      );
    case PAYMENT_TYPE:
      return (
        <PaymentRow
          key={index}
          payment={element}
          handleRowClick={handleRowClick}
          redirectToBookingDetail={redirectToBookingDetail}
          advancedMode={advancedMode}
        />
      );
    case CREDIT_NOTE_TYPE:
      return (
        <CreditNoteRow
          key={index}
          creditNote={element}
          handleRowClick={handleRowClick}
          redirectToBookingDetail={redirectToBookingDetail}
          advancedMode={advancedMode}
        />
      );
    case OVERPAYMENT_TYPE:
      return (
        <OverpaymentRow
          key={index}
          overpayment={element}
          handleRowClick={handleRowClick}
          redirectToBookingDetail={redirectToBookingDetail}
          advancedMode={advancedMode}
        />
      );
    case REFUND_TYPE:
      return (
        <RefundRow
          key={index}
          refund={element}
          handleRowClick={handleRowClick}
          redirectToBookingDetail={redirectToBookingDetail}
          advancedMode={advancedMode}
        />
      );
    case YEAR_SPLITTER:
      return (
        <YearSplitter
          key={index}
          year={element.year}
          advancedMode={advancedMode}
        />
      );
    default:
      return null;
  }
};

class ChargesTable extends React.Component<any> {
  state = {
    advancedMode: false,
    showVoidedInvoices: true
  };

  toggleMode = () => this.setState({advancedMode: !this.state.advancedMode});
  toggleInvoices = () =>
    this.setState({showVoidedInvoices: !this.state.showVoidedInvoices});

  render() {
    const {
      data,
      handleRowClick,
      redirectToBookingDetail,
      children,
      financeItemsAreLoading
    } = this.props;

    const {advancedMode, showVoidedInvoices} = this.state;

    let dataWithSplitters = getDataWithYearSplitters(data);

    if (!showVoidedInvoices) {
      dataWithSplitters = dataWithSplitters.filter(element => {
        return (
          element.type === YEAR_SPLITTER ||
          (element.type === SCHEDULE_ITEM_TYPES.INVOICE &&
            element.invoiceDetails.status != InvoiceStatusTag.VOID)
        );
      });
    }

    return (
      <div>
        <div className="ChargesTable">
          <Table>
            <Table.Header>
              <Table.HeaderItem heading="Booking" className="booking" />
              <Table.HeaderItem heading="Date" className="date" />
              {advancedMode && (
                <Table.HeaderItem heading="Due Date" className="dueDate" />
              )}
              <Table.HeaderItem heading="Type" className="type" />
              <Table.HeaderItem
                heading="Description / Reference"
                className="description"
              />
              <Table.HeaderItem heading="Category" className="category" />
              <Table.HeaderItem heading="Status" className="status" />
              {!advancedMode && (
                <Table.HeaderItem heading="Amount" className="amount" />
              )}
              {advancedMode && (
                <Table.HeaderItem heading="Due" className="due" />
              )}
              {advancedMode && (
                <Table.HeaderItem heading="Payment" className="payment" />
              )}
              <Table.HeaderItem heading="Balance" className="balance" />
              <Table.HeaderItem className="mode">
                <ButtonDropdown
                  HeaderComponent={() => <img src={switchIcon} alt="Mode" />}
                  menuPosition={ButtonDropdown.MENU_POSITION_BOTTOM_LEFT}>
                  <ButtonDropdown.Option>
                    <div className="advancedOption colab-centered colab-row-space-between">
                      <div className="colab-bold">Advanced mode</div>
                      <ToggleSwitch
                        value={advancedMode}
                        onChange={this.toggleMode}
                      />
                    </div>
                  </ButtonDropdown.Option>
                  <ButtonDropdown.Option>
                    <div className="advancedOption colab-centered colab-row-space-between">
                      <div className="colab-bold">Show Voided Invoices</div>
                      <ToggleSwitch
                        value={showVoidedInvoices}
                        onChange={this.toggleInvoices}
                      />
                    </div>
                  </ButtonDropdown.Option>
                </ButtonDropdown>
              </Table.HeaderItem>
            </Table.Header>
          </Table>
          <div className="ChargesTable__body">
            {financeItemsAreLoading && <Loading />}
            {!financeItemsAreLoading && (
              <Table>
                <Table.Body>
                  {!_.isNil(data) &&
                    dataWithSplitters.map((element, index) => (
                      <ScheduleRow
                        key={index}
                        element={element}
                        index={index}
                        handleRowClick={handleRowClick}
                        redirectToBookingDetail={redirectToBookingDetail}
                        advancedMode={advancedMode}
                      />
                    ))}
                </Table.Body>
              </Table>
            )}
          </div>
          {children}
        </div>
        <div className="ChargesTable__options">
          <ButtonDropdown small title="Schedule options">
            <ButtonDropdown.Option
              onClick={() =>
                this.props.openSelectBookingModal({
                  createItem: SCHEDULE_ITEM_TYPES.INVOICE
                })
              }>
              Create an invoice
            </ButtonDropdown.Option>
            <ButtonDropdown.Option
              onClick={() =>
                this.props.openSelectBookingModal({
                  createItem: SCHEDULE_ITEM_TYPES.CREDIT_NOTE
                })
              }>
              Create a credit note
            </ButtonDropdown.Option>
            <ButtonDropdown.Option
              onClick={() =>
                this.props.openSelectBookingModal({
                  createItem: SCHEDULE_ITEM_TYPES.OVERPAYMENT
                })
              }>
              Create an overpayment
            </ButtonDropdown.Option>
            {this.props.openBulkInvoiceVoidModal && (
              <ButtonDropdown.Option
                onClick={this.props.openBulkInvoiceVoidModal}>
                Bulk invoice void
              </ButtonDropdown.Option>
            )}
          </ButtonDropdown>
        </div>
      </div>
    );
  }
}

export default ChargesTable;
